import React, { useEffect, useState, createContext, useContext } from 'react';
import { SystemNotification } from '../types';
import { generateId } from '../utils/helpers';
interface NotificationContextType {
  notifications: SystemNotification[];
  unreadCount: number;
  addNotification: (notification: Omit<SystemNotification, 'id' | 'read' | 'createdAt'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearNotification: (id: string) => void;
  clearAllNotifications: () => void;
}
const NotificationContext = createContext<NotificationContextType | undefined>(undefined);
interface NotificationProviderProps {
  children: React.ReactNode;
}
export function NotificationProvider({
  children
}: NotificationProviderProps) {
  const [notifications, setNotifications] = useState<SystemNotification[]>(() => {
    try {
      const saved = localStorage.getItem('savidna-notifications');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter((n: any) => n && typeof n === 'object' && n.id && n.title).map((n: any) => ({
          ...n,
          // Ensure all fields are strings/primitives, not objects
          title: typeof n.title === 'string' ? n.title : String(n.title || ''),
          message: typeof n.message === 'string' ? n.message : String(n.message || ''),
          type: typeof n.type === 'string' ? n.type : 'system',
          id: typeof n.id === 'string' ? n.id : String(n.id),
          read: !!n.read,
          createdAt: new Date(n.createdAt || Date.now())
        }));
      }
      return [];
    } catch (e) {
      console.warn('⚠️ Error loading notifications from localStorage, resetting:', e);
      localStorage.removeItem('savidna-notifications');
      return [];
    }
  });
  useEffect(() => {
    localStorage.setItem('savidna-notifications', JSON.stringify(notifications));
  }, [notifications]);
  const addNotification = (notification: Omit<SystemNotification, 'id' | 'read' | 'createdAt'>) => {
    const newNotification: SystemNotification = {
      ...notification,
      id: generateId(),
      read: false,
      createdAt: new Date()
    };
    setNotifications(prev => [newNotification, ...prev]);
  };
  const markAsRead = (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? {
      ...n,
      read: true
    } : n));
  };
  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({
      ...n,
      read: true
    })));
  };
  const clearNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };
  const clearAllNotifications = () => {
    setNotifications([]);
  };
  const unreadCount = notifications.filter(n => !n.read).length;
  return <NotificationContext.Provider value={{
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    clearNotification,
    clearAllNotifications
  }} data-id="element-4708">
      {children}
    </NotificationContext.Provider>;
}
export function useNotifications(): NotificationContextType {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}